import React, { useState, useEffect, useRef } from 'react';
import { ShieldCheck, Mail, Loader2, ArrowLeft, RefreshCw, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from './ui/Button';
import { verifyEmailApi, resendVerificationApi } from '../services/apiService';
import { UserProfile } from '../types';

interface OtpVerificationFormProps {
  email: string;
  onSuccess: (user: UserProfile) => void;
  onBackToLogin: () => void;
  initialMessage?: string;
}

export const OtpVerificationForm: React.FC<OtpVerificationFormProps> = ({
  email,
  onSuccess,
  onBackToLogin,
  initialMessage,
}) => {
  const [otp, setOtp] = useState<string[]>(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(initialMessage || null);
  const [countdown, setCountdown] = useState<number>(60);
  const [canResend, setCanResend] = useState<boolean>(false);

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Focus on the first input on mount
  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  // Countdown timer for resend button
  useEffect(() => {
    let timer: any = null;
    if (countdown > 0) {
      setCanResend(false);
      timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      setCanResend(true);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [countdown]);

  const handleChange = (index: number, value: string) => {
    // Keep only digits
    const cleanVal = value.replace(/[^0-9]/g, '');
    if (!cleanVal) {
      const newOtp = [...otp];
      newOtp[index] = '';
      setOtp(newOtp);
      return;
    }

    const char = cleanVal.slice(-1);
    const newOtp = [...otp];
    newOtp[index] = char;
    setOtp(newOtp);
    setError(null);

    // Auto move to next input box
    if (index < 5 && char) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto submit if all 6 inputs filled
    const fullCode = newOtp.join('');
    if (fullCode.length === 6 && !newOtp.includes('')) {
      submitOtp(fullCode);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (!otp[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text').replace(/[^0-9]/g, '').slice(0, 6);
    if (!pastedText) return;

    const newOtp = ['', '', '', '', '', ''];
    for (let i = 0; i < pastedText.length; i++) {
      newOtp[i] = pastedText[i];
    }
    setOtp(newOtp);
    setError(null);

    const focusIdx = Math.min(pastedText.length, 5);
    inputRefs.current[focusIdx]?.focus();

    if (pastedText.length === 6) {
      submitOtp(pastedText);
    }
  };

  const submitOtp = async (codeStr?: string) => {
    const code = codeStr || otp.join('');
    if (code.length < 6) {
      setError('Vui lòng nhập đủ 6 số mã xác thực OTP.');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccessMsg(null);

    try {
      const res = await verifyEmailApi(email, code);
      if (res.user) {
        onSuccess(res.user);
      } else {
        setError('Xác thực thất bại. Vui lòng thử lại.');
      }
    } catch (err: any) {
      setError(err.message || 'Mã OTP không chính xác hoặc đã hết hạn.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!canResend || resending) return;

    setResending(true);
    setError(null);
    setSuccessMsg(null);

    try {
      const res = await resendVerificationApi(email);
      setSuccessMsg(res.message || 'Mã OTP mới đã được gửi thành công đến email của bạn.');
      setCountdown(60);
      setCanResend(false);
      setOtp(['', '', '', '', '', '']);
      if (inputRefs.current[0]) {
        inputRefs.current[0].focus();
      }
    } catch (err: any) {
      setError(err.message || 'Không thể gửi lại mã OTP. Vui lòng thử lại sau.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="w-full space-y-6 animate-in fade-in zoom-in-95 duration-300">
      {/* Icon Badge & Header */}
      <div className="flex flex-col items-center text-center space-y-2">
        <div className="w-16 h-16 rounded-2xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600 shadow-xs mb-1">
          <ShieldCheck className="w-8 h-8 text-indigo-600" />
        </div>
        <h2 className="text-xl font-extrabold text-slate-900">Xác thực địa chỉ Email</h2>
        <p className="text-xs text-slate-500 max-w-sm">
          Mã xác thực OTP 6 số đã được gửi đến email:
        </p>
        <div className="inline-flex items-center space-x-1.5 px-3 py-1 bg-slate-100 rounded-full border border-slate-200">
          <Mail className="w-3.5 h-3.5 text-indigo-600" />
          <span className="font-bold text-slate-800 text-xs">{email}</span>
        </div>
      </div>

      {/* Message & Alert Statuses */}
      {successMsg && (
        <div className="p-3 bg-emerald-50 border border-emerald-200/80 rounded-2xl text-xs text-emerald-800 flex items-start space-x-2 animate-fade-in">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
          <span className="font-medium leading-relaxed">{successMsg}</span>
        </div>
      )}

      {error && (
        <div className="p-3 bg-rose-50 border border-rose-200/80 rounded-2xl text-xs text-rose-700 flex items-start space-x-2 animate-shake">
          <AlertCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
          <span className="font-medium leading-relaxed">{error}</span>
        </div>
      )}

      {/* 6-Digit OTP Inputs */}
      <div className="space-y-3">
        <label className="block text-center text-xs font-bold text-slate-700">
          Nhập mã OTP 6 số:
        </label>
        <div className="flex items-center justify-center gap-2 sm:gap-2.5">
          {otp.map((digit, idx) => (
            <input
              key={idx}
              ref={(el) => (inputRefs.current[idx] = el)}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              disabled={loading}
              onChange={(e) => handleChange(idx, e.target.value)}
              onKeyDown={(e) => handleKeyDown(idx, e)}
              onPaste={handlePaste}
              className={`w-11 h-14 sm:w-12 sm:h-14 text-center text-xl font-black font-mono rounded-2xl border transition-all duration-150 outline-none ${
                digit
                  ? 'border-indigo-600 bg-indigo-50/50 text-indigo-950 shadow-xs ring-2 ring-indigo-500/20'
                  : 'border-slate-200 bg-white text-slate-900 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20'
              } disabled:opacity-60 disabled:bg-slate-100`}
            />
          ))}
        </div>
      </div>

      {/* Buttons & Actions */}
      <div className="space-y-3 pt-2">
        {/* Confirm Button */}
        <Button
          type="button"
          onClick={() => submitOtp()}
          disabled={loading || otp.join('').length < 6}
          className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-extrabold text-sm rounded-xl shadow-md transition-all flex items-center justify-center space-x-2 disabled:opacity-50"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin text-white" />
              <span>Đang xác thực OTP...</span>
            </>
          ) : (
            <span>Xác nhận</span>
          )}
        </Button>

        {/* Resend Code Button with Timer */}
        <div className="flex items-center justify-between pt-1 text-xs">
          <button
            type="button"
            onClick={onBackToLogin}
            className="text-slate-500 hover:text-slate-700 font-medium flex items-center space-x-1 transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span>Quay lại</span>
          </button>

          <button
            type="button"
            disabled={!canResend || resending}
            onClick={handleResend}
            className={`font-bold flex items-center space-x-1.5 transition-colors ${
              canResend && !resending
                ? 'text-indigo-600 hover:text-indigo-800 cursor-pointer'
                : 'text-slate-400 cursor-not-allowed'
            }`}
          >
            {resending ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin text-indigo-600" />
            ) : (
              <RefreshCw className="w-3.5 h-3.5" />
            )}
            <span>
              {canResend
                ? 'Gửi lại mã'
                : `Gửi lại mã (${countdown}s)`}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};
